import React, { useEffect, useState } from "react";
import Container from "../hoc/Container";
import { tgIcon, vkIcon } from "../../images";
import nintendoDealsService from "../service/nintendoDeals.service";
import Pagination from "../components/pagination";
import SelectField from "../components/common/form/selectField";
import { useResize } from "../hooks/useResize";
import {
  BanknotesIcon,
  FireIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

function NintendoDiscount() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [region, setRegion] = useState('us');
  const [totalCount, setTotalCount] = useState(0);
  const [isOutdated, setIsOutdated] = useState(false);
  
  const { isScreenSm, isScreenMd, isScreenLg } = useResize();
  const pageSize = isScreenLg ? 16 : isScreenMd ? 15 : isScreenSm ? 15 : 16;

  const regionOptions = [
    { value: 'pl', label: 'Польша' },
    { value: 'us', label: 'США' }
  ];

  const loadDeals = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nintendoDealsService.getDeals(region, currentPage, pageSize);
      setDeals(data.results);
      setTotalCount(data.count);
      
      // Проверяем, есть ли скидки
      if (data.count === 0) {
        setIsOutdated(true);
      } else {
        setIsOutdated(false);
      }
    } catch (err) {
      setError('Ошибка загрузки данных о скидках');
      console.error('Error loading deals:', err);
      setIsOutdated(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeals();
  }, [currentPage, region, pageSize]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [currentPage]);

  const handlePageChange = (pageIndex) => {
    setCurrentPage(pageIndex);
  };

  const handleRegionChange = (e) => {
    setRegion(e.value);
    setCurrentPage(1);
  };

  const formatSaleEnds = (saleEnds) => {
    if (!saleEnds) return '';
    
    const months = {
      "January": "января", "February": "февраля", "March": "марта", "April": "апреля",
      "May": "мая", "June": "июня", "July": "июля", "August": "августа",
      "September": "сентября", "October": "октября", "November": "ноября", "December": "декабря"
    };
    
    const match = saleEnds.match(/Sale ends (\w+) (\d+)/);
    if (match) {
      const monthEn = match[1];
      const day = match[2];
      const monthRu = months[monthEn] || monthEn;
      return new Date(`${monthEn} ${day}, 2024`);
    }
    
    return null;
  };

  const button = (
    <div className="flex flex-col items-center gap-2 sm:flex-row sm:flex-wrap sm:justify-center lg:flex-nowrap lg:justify-between">
      <a
        href={"https://t.me/trendy_eng_shop_bot?start=ref-001"}
        className="mb-3 flex w-40 justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-base font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 md:mb-0"
        target="_blank"
        rel="noreferrer"
        onClick={() => window.ym(93997310, "reachGoal", "tgClickNintendo")}
      >
        Купить в <img src={tgIcon} alt="TG" className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
      </a>
      <a
        href={"https://vk.com/trendy_eng_shop"}
        className="mb-3 ml-2 flex w-40 justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-base font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 md:mx-2 md:mb-0"
        target="_blank"
        rel="noreferrer"
      >
        Купить в <img src={vkIcon} alt="VK" className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
      </a>
      <a
        href={"https://trendy-eng-shop.ru/nintendo-eshop/shop"}
        className="mb-3 ml-2 flex w-40 justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-base font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 md:mx-2 md:mb-0 lg:ml-2"
        target="_blank"
        rel="noreferrer"
      >
        Купить на сайте
      </a>
    </div>
  );

  useEffect(() => {
    window.ym(93997310, "hit", "/nintendo-discount", {
      params: {
        title: "Скидки Nintendo",
        referer: "https://trendy-eng-shop.ru/",
      },
    });
  }, []);

  return (
    <Container>
      <div className="content-block">
        <h1 className="head-1">Скидки Nintendo Switch</h1>
        
        {isOutdated && (
          <div className="mb-4 text-center text-sm text-orange-600 dark:text-orange-400">
            ⚠️ Скидки могли устареть
          </div>
        )}

        <section className="mx-5 md:flex md:items-end md:justify-between">
          <div>
            <p className="mb-2 mt-4 text-center text-xl">Коды пополнения:</p>
            {button}
          </div>
          <div className="dark:dark-text my-2 flex justify-center">
            <SelectField
              label="Регион"
              value={region}
              onChange={handleRegionChange}
              options={regionOptions}
              name="region"
            />
          </div>
        </section>

        {loading ? (
          <div className="mt-8 text-center text-2xl">Загрузка скидок...</div>
        ) : error ? (
          <h3 className="mt-8 text-center text-2xl text-red-500">
            {error}
          </h3>
        ) : (
          <>
            <div className="mt-4">
              <Pagination
                itemsCount={totalCount}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
            
            <section className="m-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4">
              {!!deals.length
                ? deals.map((deal, index) => {
                    const discountEndDate = formatSaleEnds(deal.sale_ends);
                    return (
                      <div className="" key={`${deal.title}-${index}`}>
                        <a
                          href={deal.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <div className="relative">
                            <img
                              src={deal.img_url || 'https://via.placeholder.com/320x180?text=No+Image'}
                              alt={deal.title}
                              className="aspect-square w-full rounded object-contain transition hover:scale-105"
                            />
                            <div className="absolute bottom-0 left-0 rounded-sm bg-white p-1 font-mono text-sm text-black">
                              <FireIcon className="inline-block w-4 text-orange-500" />
                              {deal.percent_off}
                            </div>
                          </div>
                        </a>
                        <div className="flex justify-between pt-2">
                          <div className="flex justify-center text-base">
                            <BanknotesIcon className="inline-block w-3.5 text-green-500" />
                            <span className="ml-2 font-bold">{deal.price}</span>
                            <span className="ml-2 text-gray-500 line-through">
                              {deal.old_price}
                            </span>
                          </div>
                        </div>
                        {discountEndDate && (
                          <p className="pb-2 text-xs">
                            <ClockIcon className="mr-2 inline-block w-3.5 text-red-500" />
                            до{" "}
                            {discountEndDate.toLocaleDateString("ru", {
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        )}
                        <a
                          href={deal.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <h2 className="text-sm font-light hover:underline">
                            {deal.title}
                          </h2>
                        </a>
                      </div>
                    );
                  })
                : null}
            </section>
            
            <p className="dark:dark-text mb-1 text-center text-xs">
              {1 + pageSize * (currentPage - 1)} - {Math.min(pageSize + pageSize * (currentPage - 1), totalCount)} из {totalCount}
            </p>
            
            <Pagination
              itemsCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
            
            <div className="mt-10 text-center text-xs">
              * Данные по скидкам получены с сайта{" "}
              <a
                href="https://www.dekudeals.com"
                className="text-blue-700 dark:text-indigo-400"
                target="_blank"
                rel="noreferrer"
              >
                dekudeals.com
              </a>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}

export default NintendoDiscount;
